import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import { Body } from '../../styled components/Typography';
import {
  DefaultCheckboxLabel,
  DefaultCheckboxInput,
  RoundedCheckbox,
  SwitchCheckbox
} from './Checkbox.style';

const Checkbox = ({
  label = '',
  variant = 'default',
  disabled = false,
  defaultChecked = false,
  onChange = () => {},
  className = ''
}) => {
  const [id] = useState(uuid());

  /*
   * Render the html structure as <input/><label/> used for the switch checkbox
   */
  const renderInputBeforeLabel = () => (
    <>
      <input
        id={id}
        disabled={disabled}
        type="checkbox"
        name="public"
        tabIndex="0"
        checked={defaultChecked}
        onChange={onChange}
      />
      <label htmlFor={id}>{label && <Body tw="pl-16">{label}</Body>}</label>
    </>
  );

  const renderRoundedVariant = () => (
    <RoundedCheckbox
      className={className}
      disabled={disabled}
      data-test-type="checkbox"
      data-test-disabled={disabled}
    >
      {renderInputBeforeLabel()}
    </RoundedCheckbox>
  );

  const renderSwitchVariant = () => (
    <SwitchCheckbox
      className={className}
      disabled={disabled}
      data-test-type="checkbox"
      data-test-disabled={disabled}
    >
      {renderInputBeforeLabel()}
    </SwitchCheckbox>
  );

  /*
   * For the default checkbox render the html structure as <label><input/></label>
   *
   */
  const renderDefaultVariant = () => (
    <DefaultCheckboxLabel className={className}>
      <DefaultCheckboxInput
        disabled={disabled}
        type="checkbox"
        name="public"
        tabIndex="0"
        checked={defaultChecked}
        onChange={onChange}
      />
      {label && <Body tw="ml-16 min-w-0">{label}</Body>}
    </DefaultCheckboxLabel>
  );

  switch (variant) {
    case 'rounded':
      return renderRoundedVariant();
    case 'switch':
      return renderSwitchVariant();
    default:
      return renderDefaultVariant();
  }
};

Checkbox.propTypes = {
  /** ClassName is supported such that styled components can pass extra styling.
   * Do not use this to adjust the styling, because the ordering of the applied tailwind classes is undeterministic */
  className: PropTypes.string,
  label: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'rounded', 'switch']),
  disabled: PropTypes.bool,
  defaultChecked: PropTypes.bool,
  onChange: PropTypes.func
};

export default Checkbox;
